import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  RotateCcw,
  CheckCircle2,
  Info,
  Loader2,
  ArrowRight,
  BookOpenCheck,
  Search,
  LockKeyhole,
} from "lucide-react";
import { toast } from "sonner";
import StudyShell from "@/components/study/StudyShell";
import ProtectedContent from "@/components/study/ProtectedContent";
import {
  getFlashcards,
  generateFlashcards,
  setFlashcardProgress,
  getStudyQuestions,
  trackEvent,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import { useStudyAuth } from "@/context/StudyAuth";

const PORTION_LABELS = {
  state: "Illinois State",
  national: "National",
  math: "Math",
};

const STATUS_LABELS = {
  new: "New",
  review: "Review again",
  known: "Got it",
};

const normalize = (value) => String(value || "").toLowerCase().trim();

function buildConcepts(questions) {
  const map = new Map();
  (questions || []).forEach((q) => {
    const topic = q.topic || "General";
    const key = `${q.portion}:${topic}`;
    const entry = map.get(key) || {
      topic,
      portion: q.portion,
      label: PORTION_LABELS[q.portion] || "Mixed",
      count: 0,
      difficulties: {},
    };
    entry.count += 1;
    entry.difficulties[q.difficulty || "medium"] =
      (entry.difficulties[q.difficulty || "medium"] || 0) + 1;
    map.set(key, entry);
  });
  return Array.from(map.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.topic.localeCompare(b.topic);
  });
}

function ConceptIndex({ concepts }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return concepts;
    return concepts.filter(
      (c) =>
        normalize(c.topic).includes(q) ||
        normalize(c.label).includes(q) ||
        normalize(c.portion).includes(q)
    );
  }, [concepts, query]);
  const visible = filtered.slice(0, 36);

  if (!concepts.length) return null;

  return (
    <section className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
            <BookOpenCheck className="h-4 w-4" /> Concept index
          </h2>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
            Browse the topics that appear across the active question bank.
          </p>
        </div>
        <label className="relative block w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:rgba(11,18,32,0.45)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search word bank"
            className="h-10 w-full rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white pl-9 pr-3 text-sm outline-none focus:border-[var(--ibp-navy-950)]"
          />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visible.map((c) => (
          <Link
            key={`${c.portion}:${c.topic}`}
            to={`/study/question-bank?topic=${encodeURIComponent(c.topic)}`}
            className="rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-offwhite)] px-4 py-3 transition-colors hover:border-[var(--ibp-navy-950)] hover:bg-white"
          >
            <span className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-sm font-semibold text-[var(--ibp-ink)]">
                  {c.topic}
                </span>
                <span className="mt-1 block text-xs font-medium text-[color:rgba(11,18,32,0.6)]">
                  {c.label}
                </span>
              </span>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[var(--ibp-ink)]">
                {c.count}
              </span>
            </span>
          </Link>
        ))}
      </div>
      {filtered.length > visible.length ? (
        <p className="mt-3 text-xs text-[color:rgba(11,18,32,0.5)]">
          Showing {visible.length} of {filtered.length} matching concepts. Search to narrow the list.
        </p>
      ) : null}
    </section>
  );
}

function Deck({ cards, onProgress }) {
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);

  // Keep index in range if the deck shrinks/grows.
  useEffect(() => {
    if (i > cards.length - 1) setI(0);
  }, [cards.length, i]);

  const card = cards[i];

  const advance = () => {
    setFlipped(false);
    setI((prev) => (prev + 1 < cards.length ? prev + 1 : 0));
  };

  const mark = async (status) => {
    if (!card) return;
    try {
      await onProgress(card.id, status);
    } catch (e) {
      // non-blocking
    }
    advance();
  };

  if (!card) return null;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[var(--ibp-ink)]">
            Card {i + 1} of {cards.length}
          </p>
          <p className="mt-0.5 text-xs text-[color:rgba(11,18,32,0.55)]">
            {PORTION_LABELS[card.portion] || "Mixed"} {card.topic ? `- ${card.topic}` : ""}
          </p>
        </div>
        <span
          className={[
            "rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
            card.status === "known"
              ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
              : card.status === "review"
              ? "bg-[color:rgba(214,162,30,0.18)] text-[#9a6b00]"
              : "bg-[var(--ibp-offwhite)] text-[color:rgba(11,18,32,0.55)]",
          ].join(" ")}
        >
          {STATUS_LABELS[card.status] || STATUS_LABELS.new}
        </span>
      </div>

      <ProtectedContent
        data-testid={STUDY.flashcard}
        className="mt-3"
      >
        <button
          type="button"
          data-testid={STUDY.flashcardFlip}
          onClick={() => setFlipped((f) => !f)}
          aria-label="Flip card"
          className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-8 text-center shadow-[0_10px_30px_rgba(7,20,39,0.06)] transition-colors hover:border-[var(--ibp-navy-950)]"
        >
          {!flipped ? (
            <>
              <span className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(11,18,32,0.45)]">
                Term
              </span>
              <span className="mt-3 text-2xl font-bold leading-8 text-[var(--ibp-ink)]">
                {card.term}
              </span>
              <span className="mt-5 text-xs text-[color:rgba(11,18,32,0.5)]">
                Tap to reveal the definition
              </span>
            </>
          ) : (
            <>
              <span className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(11,18,32,0.45)]">
                Definition
              </span>
              <span className="mt-3 text-base leading-7 text-[color:rgba(11,18,32,0.85)]">
                {card.definition}
              </span>
              {card.source_reference ? (
                <span className="mt-4 text-xs text-[color:rgba(11,18,32,0.5)]">
                  Source: {card.source_reference}
                </span>
              ) : null}
            </>
          )}
        </button>
      </ProtectedContent>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          data-testid={STUDY.flashcardReview}
          onClick={() => mark("review")}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-5 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[color:rgba(10,27,51,0.05)]"
        >
          <RotateCcw className="h-4 w-4 text-[var(--ibp-gold-500)]" /> Review again
        </button>
        <button
          type="button"
          data-testid={STUDY.flashcardKnown}
          onClick={() => mark("known")}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:opacity-90"
        >
          <CheckCircle2 className="h-4 w-4 text-[#7ee2a8]" /> Got it
        </button>
      </div>

      <button
        type="button"
        onClick={advance}
        className="mx-auto mt-4 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:bg-[color:rgba(10,27,51,0.06)]"
      >
        Skip <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function StudyFlashcards() {
  const { hasFullAccess } = useStudyAuth();
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [portionFilter, setPortionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["flashcards"],
    queryFn: getFlashcards,
  });
  const { data: questionBank } = useQuery({
    queryKey: ["word-bank-question-index"],
    queryFn: () => getStudyQuestions({ mode: "all" }),
  });

  useEffect(() => {
    trackEvent("word_bank_opened");
  }, []);

  const cards = useMemo(() => (data && data.cards) || [], [data]);
  const usage = data && data.usage;
  const concepts = useMemo(() => buildConcepts(questionBank || []), [questionBank]);
  const filteredCards = useMemo(() => {
    const query = normalize(searchQuery);
    return cards.filter((card) => {
      const portionMatches = portionFilter === "all" || card.portion === portionFilter;
      const statusMatches = statusFilter === "all" || card.status === statusFilter;
      const searchMatches =
        !query ||
        normalize(card.term).includes(query) ||
        normalize(card.definition).includes(query) ||
        normalize(card.topic).includes(query);
      return portionMatches && statusMatches && searchMatches;
    });
  }, [cards, portionFilter, searchQuery, statusFilter]);
  const progress = useMemo(
    () =>
      cards.reduce(
        (counts, card) => {
          counts[card.status] = (counts[card.status] || 0) + 1;
          return counts;
        },
        { new: 0, review: 0, known: 0 }
      ),
    [cards]
  );

  const onProgress = async (cardId, status) => {
    await setFlashcardProgress(cardId, status);
    trackEvent("flashcard_progress", { status });
    // Optimistic local update without a full refetch
    qc.setQueryData(["flashcards"], (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cards: prev.cards.map((c) => (c.id === cardId ? { ...c, status } : c)),
      };
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateFlashcards();
      if (!res.enabled) {
        toast("Flashcard generation isn't available yet", {
          description: res.reply || "AI key not configured.",
        });
      } else if (res.generated > 0) {
        toast.success(`Generated ${res.generated} new flashcard(s)`);
        trackEvent("flashcards_generated", { generated: res.generated });
        qc.invalidateQueries({ queryKey: ["flashcards"] });
      } else {
        toast(res.reply || "No new cards to generate right now.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate flashcards. Please try again.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const canGenerateMore = data ? data.can_generate_more : true;

  return (
    <StudyShell testId={STUDY.flashcardsPage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
            Vocabulary and concepts
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">Word Bank</h1>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
            Review the essential terms behind the questions, then use your progress to revisit what has not stuck yet.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/study/learn"
            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:border-[var(--ibp-navy-950)]"
          >
            <BookOpenCheck className="h-4 w-4" /> Learn by lesson
          </Link>
          {hasFullAccess && data?.enabled && canGenerateMore && (
            <button
              type="button"
              data-testid={STUDY.flashcardsGenerate}
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-5 py-2 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Add AI-generated cards
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {!hasFullAccess ? (
        <div className="mt-5 flex flex-col gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
            Your free account includes {data?.total || 40} terms. Full access opens the complete Word Bank and future additions.
          </p>
          <Link to="/product" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline">
            <LockKeyhole className="h-4 w-4" /> Unlock all terms
          </Link>
        </div>
      ) : null}

      <div
        data-testid={STUDY.flashcardsAiNote}
        className="mt-4 flex items-start gap-2 rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-white px-3.5 py-2.5"
      >
        <Info className="mt-0.5 h-4 w-4 flex-none text-[var(--ibp-gold-600,#b8860b)]" />
        <p className="text-xs leading-5 text-[color:rgba(11,18,32,0.75)]">
          <span className="font-semibold">Curated core deck.</span>{" "}
          {(data && data.ai_note) ||
            "Core cards are curated from the lesson library and reviewed sources. Always verify current Illinois rules with official sources."}
        </p>
      </div>

      {hasFullAccess && data?.enabled && usage ? (
        <p className="mt-2 text-xs text-[color:rgba(11,18,32,0.5)]">
          Optional AI expansion used: {usage.daily_used}/{usage.daily_cap} today · {usage.total_used}/
          {usage.total_cap} total
        </p>
      ) : null}

      {isLoading ? (
        <p className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">
          Loading word bank...
        </p>
      ) : cards.length === 0 ? (
        <div
          data-testid={STUDY.flashcardsEmpty}
          className="mt-6 rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center"
        >
          <p className="text-base font-semibold text-[var(--ibp-ink)]">Word Bank is getting ready</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-[color:rgba(11,18,32,0.6)]">
            Your curated vocabulary deck will be available as soon as the study platform finishes preparing it.
          </p>
        </div>
      ) : (
        <>
          <section className="mt-6 overflow-hidden rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
            <div className="grid grid-cols-3 divide-x divide-[color:rgba(10,27,51,0.1)]">
              <div className="px-4 py-3">
                <p className="text-lg font-bold text-[var(--ibp-ink)]">{data?.core_total || cards.length}</p>
                <p className="text-xs text-[color:rgba(11,18,32,0.55)]">core terms</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-lg font-bold text-[#15803d]">{progress.known}</p>
                <p className="text-xs text-[color:rgba(11,18,32,0.55)]">got it</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-lg font-bold text-[var(--ibp-ink)]">{progress.review + progress.new}</p>
                <p className="text-xs text-[color:rgba(11,18,32,0.55)]">to review</p>
              </div>
            </div>
          </section>

          <section className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="text-xs font-semibold text-[color:rgba(11,18,32,0.62)]">Section</span>
                <select
                  value={portionFilter}
                  onChange={(e) => setPortionFilter(e.target.value)}
                  className="mt-1 h-10 w-full rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
                >
                  <option value="all">All sections</option>
                  <option value="state">Illinois State</option>
                  <option value="national">National</option>
                  <option value="math">Math</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-[color:rgba(11,18,32,0.62)]">Progress</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mt-1 h-10 w-full rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
                >
                  <option value="all">All cards</option>
                  <option value="new">New</option>
                  <option value="review">Review again</option>
                  <option value="known">Got it</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-[color:rgba(11,18,32,0.62)]">Find a term</span>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search the deck"
                  className="mt-1 h-10 w-full rounded-xl border border-[color:rgba(10,27,51,0.15)] bg-white px-3 text-sm text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
                />
              </label>
            </div>
          </section>

          {filteredCards.length ? (
            <Deck
              key={`${portionFilter}-${statusFilter}-${searchQuery}`}
              cards={filteredCards}
              onProgress={onProgress}
            />
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-12 text-center">
              <p className="text-base font-semibold text-[var(--ibp-ink)]">No cards match those filters</p>
              <button
                type="button"
                onClick={() => {
                  setPortionFilter("all");
                  setStatusFilter("all");
                  setSearchQuery("");
                }}
                className="mt-3 text-sm font-semibold text-[var(--ibp-navy-950)] underline underline-offset-4"
              >
                Clear filters
              </button>
            </div>
          )}

          <ConceptIndex concepts={concepts} />
        </>
      )}
    </StudyShell>
  );
}
